import React, { useState, useEffect } from 'react';
import styles from './OpponentPanel.module.css';
import { usePuzzleState } from '../../context/PuzzleStateContext';
import { useMetadata } from '../../context/MetadataContext';

const OpponentPanel = ({ clockTime }) => {
  const [completedMoves, setCompletedMoves] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const { puzzleState  } = usePuzzleState();
  const { metadata, getText } = useMetadata();
  const move_timings = puzzleState?.stats?.opponent_move_timings;
  const move_count = puzzleState?.stats?.opponent_move_count;
  const country_code = puzzleState?.stats?.opponent_country_code;
  const nickname = puzzleState?.stats?.opponent_nickname;
  
  // Calculate how many moves should be completed based on current time
  useEffect(() => {
    // Update when component mounts and when clockTime changes
    const calculateCompletedMoves = () => {
      if (!move_timings || move_timings.length === 0) {
        return 0;
      }

      // Current elapsed time in seconds
      const currentElapsedTime = clockTime;
      
      // Count how many moves should be completed by now
      let completed = 0;
      for (let i = 0; i < move_timings.length; i++) {
        if (currentElapsedTime >= move_timings[i]) {
          completed++;
        } else {
          break; // Stop counting once we reach a move that hasn't happened yet
        }
      }
      
      return completed;
    };

    // Initial calculation
    setCompletedMoves(calculateCompletedMoves());
    setLastUpdateTime(Date.now());

    // Recalculate completed moves whenever clockTime changes
    setCompletedMoves(calculateCompletedMoves());
    setLastUpdateTime(Date.now());
  }, [move_timings, clockTime, lastUpdateTime]);

  // If no move data, don't render anything
  if (!move_timings) {
    return null;
  }

  // Generate progress bar segments
  const renderProgressBar = () => {
    if (!move_count) return null;
    
    const segments = [];
    for (let i = 0; i < move_count; i++) {
      segments.push(
        <div 
          key={i} 
          className={`${styles.progressSegment} ${i < completedMoves ? styles.completed : ''}`}
        />
      );
    }
    
    return (
      <div className={styles.progressBar}>
        {segments}
      </div>
    );
  };

  // Get country flag emoji
  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '';
    
    // Convert country code to flag emoji
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className={styles.opponentPanel}>
      <div className={styles.opponentInfo}>
        <div className={styles.nameWithFlag}>
          <span className={styles.flag}>{getFlagEmoji(country_code)}</span>
          <span className={styles.name}>{nickname}</span>
        </div>
        <div className={styles.moveCountContainer}>
          <span className={styles.moveCount}>{getText('common_text.moves')}: {completedMoves}/{move_count}</span>
        </div>
      </div>
      {renderProgressBar()}
    </div>
  );
};

export default OpponentPanel;